/**
 * SelectAllMatchingBanner — the trigger for extending a page-scoped bulk
 * selection to every media unit matching the active catalog filter.
 *
 * Renders as a small floating pill anchored above the shared
 * `BulkActionToolbar` (fixed to the bottom of the viewport, reachable without
 * scrolling the table — the same "reachable without scrolling" requirement
 * that motivated moving the bulk-edit/mark-availability triggers into that
 * toolbar) as soon as at least one row is checked and there are more rows
 * matching the active filter than are currently checked — checking every row
 * on the current page is NOT a precondition. Clicking the link hands off to
 * `useMediaBulkSelection.activateSelectAllFilter` (via
 * `useMediaUnitsPage.bulkActivateSelectAllFilter`), which clears the checked
 * id set and switches the selection into filter-based mode — the same mode
 * `BulkEditAction`/`BulkEditDrawer` already consume via `selectAllFilter`.
 *
 * The route only renders this banner in the pre-activation "opportunity"
 * state (`selectAllFilter == null`); once filter-based mode is active, the
 * floating `BulkActionToolbar` itself shows the full matching count and its
 * own clear-selection control, so the banner is no longer rendered.
 *
 * Traceability: bulk-edit-media / Requirement: "Select all N matching"
 * switches to filter-based selection.
 */
import type { TFunction } from 'i18next';
import type { MediaUnitFilters } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';

export interface SelectAllMatchingBannerProps {
  /** Active "select all matching" filter, when the user already activated it. */
  selectAllFilter: MediaUnitFilters | null;
  /** Number of rows currently checked via the checkbox column. */
  selectedCount: number;
  /** Total rows matching the active catalog filter. */
  totalMatching: number;
  onActivateSelectAllFilter: () => void;
  onClearSelection: () => void;
  t: TFunction<'inventory'>;
}

export function SelectAllMatchingBanner({
  selectAllFilter,
  selectedCount,
  totalMatching,
  onActivateSelectAllFilter,
  onClearSelection,
  t,
}: SelectAllMatchingBannerProps) {
  if (selectAllFilter == null && (selectedCount === 0 || totalMatching <= selectedCount)) {
    return null;
  }

  return (
    <div
      className="fixed bottom-24 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-xs text-muted-foreground shadow-lg"
      data-testid="select-all-matching-banner"
    >
      {selectAllFilter != null ? (
        <>
          <span>
            {totalMatching} {t('bulkEdit.selectedCountSuffix')}
          </span>
          <Button
            type="button"
            variant="inline"
            data-testid="select-all-matching-clear"
            onClick={onClearSelection}
          >
            {t('bulkEdit.clearSelectionLabel')}
          </Button>
        </>
      ) : (
        <>
          <span>
            {selectedCount} {t('bulkEdit.selectedCountSuffix')}
          </span>
          <Button
            type="button"
            variant="inline"
            data-testid="select-all-matching-trigger"
            onClick={onActivateSelectAllFilter}
          >
            {t('bulkEdit.selectAll', { count: totalMatching })}
          </Button>
        </>
      )}
    </div>
  );
}
